import type { ChangeEventHandler, Ref } from "react";
import { forwardRef, useEffect, useState } from "react";
// eslint-disable-next-line camelcase -- импортированное значение
import { VisuallyHidden, unstable_useId } from "reakit";

import { Box } from "../../Box/Box";
import { Field } from "../../Field/Field";
import { Inline } from "../../Inline/Inline";
import CalendarPopover from "../CalendarPopover/CalendarPopover";
import { transformDateToInputFormat, transformSingleDateToStandard } from "../helpers";
import type { SingleFieldDatepickerProps } from "../types";

export const SingleFieldDatepicker = forwardRef(
  (
    {
      disabled,
      hasError = false,
      value,
      minDate,
      maxDate,
      buttonKind,
      disabledDates,
      initialCalendarType,
      className,
      onChange,
      ...fieldProps
    }: SingleFieldDatepickerProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const [innerValue, setInnerValue] = useState(value);

    const { id } = unstable_useId();

    useEffect(() => {
      setInnerValue(value);
    }, [value]);

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const { value: inputDate } = event.target;
      setInnerValue(inputDate);
      onChange(inputDate);
    };

    const handleCalendarChange = (date: string | undefined) => {
      let inputDate = date ? transformDateToInputFormat(date) : "";
      setInnerValue(inputDate);
      onChange(inputDate);
    };

    return (
      <Inline space="datepickerSpace" alignItems="flex-end" className={className}>
        <Box>
          <Field
            {...fieldProps}
            ref={ref}
            aria-autocomplete="none"
            aria-describedby={innerValue ? undefined : id}
            placeholder="ДД.ММ.ГГГГ"
            disabled={disabled}
            hasError={hasError}
            value={innerValue}
            onChange={handleInputChange}
            styles={{
              width: "datepickerSingleFieldWidth",
              fontFeatureSettings: '"tnum"',
            }}
          />
          <VisuallyHidden id={id}>разделитель точка</VisuallyHidden>
        </Box>
        <CalendarPopover
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          disabledDates={disabledDates}
          buttonKind={buttonKind}
          initialCalendarType={initialCalendarType}
          selectedDate={transformSingleDateToStandard(innerValue)}
          onSelectedDateChange={handleCalendarChange}
        />
      </Inline>
    );
  }
);

SingleFieldDatepicker.displayName = "SingleFieldDatepicker";
